import type { WorkerRecord } from '../lib/reports';
import { formatDate, formatHours, formatRs } from '../lib/money';
import { DocHeader } from '../components/PrintPreview';
import StaffStamp from '../components/StaffStamp';
import SignatureImage from '../components/SignatureImage';
import { signatureApi } from '../lib/signatures';

/**
 * The worker's earnings statement, A4.
 *
 * "Every day with a plain-English working, a 'How the total earnings are made
 * up' block, the EPF/ETF block with membership dates, what has been remitted
 * for him, and the pay runs he appears in."
 *
 * The plain-English working is the point of the document: a man who cannot
 * check arithmetic can still read "6 of 8 hours at Rs 4,500/day" and tell
 * whether it matches the day he worked.
 */

interface Props {
  record: WorkerRecord;
  printedBy: string;
  printedAt: Date;
  /** Who is printing it — "Checked by (office)", with their saved signature. */
  checkedBy?: { id: string; name: string; at: string };
  /** His acknowledgement, signed on the tablet before it printed. */
  signature?: { id: string; signedAt: string } | null;
}

export default function WorkerStatement({ record, printedBy, printedAt, checkedBy, signature }: Props) {
  const days = [...record.days]
    .filter((day) => day.status === 'approved')
    .sort((a, b) => a.work_date.localeCompare(b.work_date));

  const advancesByDate = new Map<string, { amount: number; notes: string[] }>();
  for (const advance of record.advances) {
    const held = advancesByDate.get(advance.work_date) ?? { amount: 0, notes: [] };
    held.amount += advance.amount_rs;
    if (advance.note) held.notes.push(advance.note);
    advancesByDate.set(advance.work_date, held);
  }

  const totals = days.reduce(
    (acc, day) => ({
      days: acc.days + (day.earned_rs > 0 || day.pay_basis === 'day' ? 1 : 0),
      otTenths: acc.otTenths + day.ot_tenths,
      salary: acc.salary + day.salary_rs,
      ot: acc.ot + day.ot_rs,
      allowance: acc.allowance + day.allowance_rs,
      sunday: acc.sunday + day.sunday_allowance_rs,
      gross: acc.gross + day.earned_rs,
      base: acc.base + day.base_earned_rs,
    }),
    { days: 0, otTenths: 0, salary: 0, ot: 0, allowance: 0, sunday: 0, gross: 0, base: 0 },
  );

  const advancesTotal = record.advances.reduce((sum, a) => sum + a.amount_rs, 0);
  const epfEmployee = record.contributions.reduce((sum, c) => sum + c.epf_employee_rs, 0);
  const credited = record.contributions.reduce((sum, c) => sum + c.epf_total_rs + c.etf_rs, 0);
  const remitted = record.contributions
    .filter((c) => c.paid_on !== null)
    .reduce((sum, c) => sum + c.epf_total_rs + c.etf_rs, 0);

  const paidRuns = record.payRuns.filter((run) => run.status === 'approved');
  const paidGross = paidRuns.reduce((sum, run) => sum + run.gross_rs, 0);
  const paidNet = paidRuns.reduce((sum, run) => sum + run.net_rs, 0);

  const member = record.memberships.length > 0;
  const showEpf = member && totals.base > 0;

  return (
    <>
      <DocHeader
        doc="Employee earnings statement"
        title={record.worker.name}
        sub={
          `${record.roles.map((r) => `${r.name} ${formatRs(r.daily_rate_rs)}/day`).join('  ·  ')}` +
          (days.length > 0
            ? `  ·  ${formatDate(days[0]!.work_date)} to ${formatDate(days[days.length - 1]!.work_date)}`
            : '') +
          `  ·  printed ${printedAt.toLocaleString('en-GB')} by ${printedBy}`
        }
      />

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Job done and how it was worked out</th>
            <th className="num">Hrs</th>
            <th className="num">OT h</th>
            <th className="num">Salary</th>
            <th className="num">OT pay</th>
            <th className="num">Allowance</th>
            <th className="num">Earned</th>
            <th className="num">Base</th>
            <th className="num">Advance</th>
            <th>Pay run</th>
          </tr>
        </thead>
        <tbody>
          {days.length === 0 && <tr><td colSpan={11}>Nothing recorded.</td></tr>}
          {days.map((day) => {
            const advance = advancesByDate.get(day.work_date);
            return (
              <tr key={day.work_date}>
                <td>{formatDate(day.work_date)}</td>
                <td>
                  {day.role_name ?? '—'}
                  <div style={{ fontSize: 10 }}>{working(day, advance?.notes ?? [])}</div>
                </td>
                <td className="num">
                  {day.pay_basis === 'trip' ? '—' : formatHours(day.hours_tenths)}
                </td>
                <td className="num">{day.ot_tenths ? formatHours(day.ot_tenths) : '—'}</td>
                <td className="num">{day.salary_rs ? formatRs(day.salary_rs) : '—'}</td>
                <td className="num">{day.ot_rs ? formatRs(day.ot_rs) : '—'}</td>
                <td className="num">
                  {day.allowance_rs + day.sunday_allowance_rs
                    ? formatRs(day.allowance_rs + day.sunday_allowance_rs)
                    : '—'}
                </td>
                <td className="num">{formatRs(day.earned_rs)}</td>
                <td className="num">{day.base_earned_rs ? formatRs(day.base_earned_rs) : '—'}</td>
                <td className="num">{advance ? `−${formatRs(advance.amount)}` : '—'}</td>
                <td>
                  {day.pay_run_id
                    ? day.run_status === 'approved'
                      ? `paid ${formatDate(day.run_period_to!)}`
                      : 'draft run'
                    : 'unpaid'}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={2}>Totals — {totals.days} days worked</th>
            <th className="num">—</th>
            <th className="num">{formatHours(totals.otTenths)}</th>
            <th className="num">{formatRs(totals.salary)}</th>
            <th className="num">{formatRs(totals.ot)}</th>
            <th className="num">{formatRs(totals.allowance + totals.sunday)}</th>
            <th className="num">{formatRs(totals.gross)}</th>
            <th className="num">{formatRs(totals.base)}</th>
            <th className="num">−{formatRs(advancesTotal)}</th>
            <th />
          </tr>
        </tfoot>
      </table>

      <h4 style={{ marginTop: 14 }}>How the total earnings are made up</h4>
      <table>
        <tbody>
          <tr>
            <td>Daily salary for {totals.days} days worked</td>
            <td className="num">{formatRs(totals.salary)}</td>
          </tr>
          <tr>
            <td>
              Overtime — {formatHours(totals.otTenths)} hours at the day salary divided by 8
            </td>
            <td className="num">{formatRs(totals.ot)}</td>
          </tr>
          <tr>
            <td>Allowances</td>
            <td className="num">{formatRs(totals.allowance)}</td>
          </tr>
          <tr>
            <td>Sunday allowances</td>
            <td className="num">{formatRs(totals.sunday)}</td>
          </tr>
          <tr>
            <th>Gross earnings</th>
            <th className="num">{formatRs(totals.gross)}</th>
          </tr>
          <tr>
            <td>Less advances taken in cash</td>
            <td className="num">−{formatRs(advancesTotal)}</td>
          </tr>
          {epfEmployee > 0 && (
            <tr>
              <td>Less EPF employee share</td>
              <td className="num">−{formatRs(epfEmployee)}</td>
            </tr>
          )}
          <tr>
            <th>Net due for the whole period</th>
            <th className="num">{formatRs(totals.gross - advancesTotal - epfEmployee)}</th>
          </tr>
          <tr>
            <td>Already paid in pay runs</td>
            <td className="num">{formatRs(paidNet)}</td>
          </tr>
          <tr>
            <th>Still owed</th>
            <th className="num">
              {formatRs(totals.gross - advancesTotal - epfEmployee - paidNet)}
            </th>
          </tr>
        </tbody>
      </table>

      {/* Hidden entirely for a non-member and for a member with no contribution.
          Never rows of zeros. */}
      {showEpf && (
        <>
          <h4 style={{ marginTop: 14 }}>
            EPF and ETF
            {record.memberships[0]?.epf_no && ` — member number ${record.memberships[0].epf_no}`}
          </h4>
          <div style={{ fontSize: 11, marginBottom: 4 }}>
            Worked out on base salary only; overtime, allowances and Sunday money are not
            included.
            {record.memberships.map((m) => (
              <span key={m.member_from}>
                {' '}Member from {formatDate(m.member_from)}
                {m.stopped_on ? `, stopped ${formatDate(m.stopped_on)}.` : '.'}
              </span>
            ))}
          </div>

          <table>
            <tbody>
              <tr>
                <td>Base salary earned in this period</td>
                <td className="num">{formatRs(totals.base)}</td>
              </tr>
              <tr>
                <td>EPF employee share — deducted from his pay</td>
                <td className="num">{formatRs(epfEmployee)}</td>
              </tr>
              <tr>
                <td>EPF employer share — paid by the quarry</td>
                <td className="num">
                  {formatRs(record.contributions.reduce((s, c) => s + c.epf_employer_rs, 0))}
                </td>
              </tr>
              <tr>
                <td>ETF — paid by the quarry</td>
                <td className="num">
                  {formatRs(record.contributions.reduce((s, c) => s + c.etf_rs, 0))}
                </td>
              </tr>
              <tr>
                <th>Total credited to his fund</th>
                <th className="num">{formatRs(credited)}</th>
              </tr>
            </tbody>
          </table>

          {/* "what has been remitted for him, with dates and references, and
              what is still to be paid" */}
          <table style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Paid to the fund for him</th>
                <th>Paid on</th>
                <th>Reference</th>
                <th className="num">EPF</th>
                <th className="num">ETF</th>
              </tr>
            </thead>
            <tbody>
              {record.contributions.filter((c) => c.paid_on).length === 0 && (
                <tr><td colSpan={5}>Nothing remitted for him yet.</td></tr>
              )}
              {record.contributions
                .filter((c) => c.paid_on)
                .map((c) => (
                  <tr key={c.pay_run_id}>
                    <td>{c.period_month}</td>
                    <td>{formatDate(c.paid_on!)}</td>
                    <td>{c.reference ?? '—'}</td>
                    <td className="num">{formatRs(c.epf_total_rs)}</td>
                    <td className="num">{formatRs(c.etf_rs)}</td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={3}>Remitted</th>
                <th className="num" colSpan={2}>{formatRs(remitted)}</th>
              </tr>
              <tr>
                <th colSpan={3}>Still to be paid to the fund</th>
                <th className="num" colSpan={2}>{formatRs(credited - remitted)}</th>
              </tr>
            </tfoot>
          </table>
        </>
      )}

      {!member && (
        <div style={{ marginTop: 10, fontSize: 12 }}>Not registered for EPF or ETF.</div>
      )}

      {record.payRuns.length > 0 && (
        <>
          <h4 style={{ marginTop: 14 }}>Pay runs he appears in</h4>
          <table>
            <thead>
              <tr>
                <th>Period</th>
                <th>Status</th>
                <th className="num">Gross</th>
                <th className="num">Net paid</th>
              </tr>
            </thead>
            <tbody>
              {record.payRuns.map((run) => (
                <tr key={run.id}>
                  <td>
                    {formatDate(run.period_from)} – {formatDate(run.period_to)}
                  </td>
                  <td>{run.status === 'approved' ? 'approved' : 'unapproved'}</td>
                  <td className="num">{formatRs(run.gross_rs)}</td>
                  <td className="num">{formatRs(run.net_rs)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2}>Approved runs</th>
                <th className="num">{formatRs(paidGross)}</th>
                <th className="num">{formatRs(paidNet)}</th>
              </tr>
            </tfoot>
          </table>
        </>
      )}

      <div className="siglines">
        <StaffStamp label="Checked by (office)" userId={checkedBy?.id} name={checkedBy?.name} at={checkedBy?.at} />
        {signature ? (
          <div className="stamp-line">
            <div>Employee signature:</div>
            <div className="stamp-emp">
              <SignatureImage path={signatureApi.statementPath(signature.id)} />
            </div>
            <div className="stamp-who">
              {record.worker.name} · signed on the tablet {new Date(signature.signedAt).toLocaleString('en-GB')}
            </div>
          </div>
        ) : (
          <div>Employee signature: ______________________</div>
        )}
      </div>
    </>
  );
}

/** "6 of 8 hours at Rs 4,500/day; 2h overtime at Rs 563/h; allowance for …" */
function working(day: WorkerRecord['days'][number], advanceNotes: string[]): string {
  const parts: string[] = [];
  const dailyRate = day.hours_tenths > 0 ? Math.round((day.salary_rs * 80) / day.hours_tenths) : 0;

  if (day.pay_basis === 'trip') {
    parts.push('no day salary, paid by allowance');
  } else if (day.salary_rs > 0) {
    parts.push(
      day.hours_tenths === 80
        ? `full day at ${formatRs(dailyRate)}/day`
        : `${formatHours(day.hours_tenths)} of 8 hours at ${formatRs(dailyRate)}/day`,
    );
  }
  if (day.ot_rs > 0) {
    const perHour = Math.round((day.ot_rs * 10) / day.ot_tenths);
    parts.push(`${formatHours(day.ot_tenths)}h overtime at ${formatRs(perHour)}/h`);
  }
  if (day.allowance_rs > 0) {
    parts.push(day.allowance_reason ? `allowance for ${day.allowance_reason}` : 'allowance');
  }
  if (day.sunday_allowance_rs > 0) parts.push('Sunday allowance');
  for (const note of advanceNotes) parts.push(`advance taken — ${note}`);

  return parts.join('; ');
}
